/**
 * 009 — Moderación del álbum de invitados (US2 + US4).
 * Vista de la pareja: todas las fotos con estado y atribución (FR-011: la autoría es
 * SOLO para la pareja; los invitados no la ven). Aprobar/ocultar 1-a-1 y en lote,
 * eliminar permanente con confirmación. Indicador de pendientes (FR-015).
 */
import { useState } from 'react';
import { Eye, EyeOff, Check, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useAllGuests } from '@/hooks/useGuests';
import {
  useAllGuestPhotos, useModerateGuestPhoto, useModerateAlbumBatch, useDeleteGuestPhoto,
  type OwnerAlbumPhoto,
} from '@/hooks/useGuestAlbum';
import { photoState } from '@/invitation/album/albumRules';

interface Props {
  invitationId: string;
}

const STATE_LABEL: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
  approved: { label: 'Aprobada', variant: 'default' },
  pending: { label: 'Pendiente', variant: 'secondary' },
  hidden: { label: 'Oculta', variant: 'outline' },
};

const AlbumModeration = ({ invitationId }: Props) => {
  const { data: photos = [] } = useAllGuestPhotos(invitationId);
  const { data: allGuests = [] } = useAllGuests();
  const guests = allGuests.filter((g) => g.invitation_id === invitationId);
  const moderate = useModerateGuestPhoto(invitationId);
  const batch = useModerateAlbumBatch(invitationId);
  const del = useDeleteGuestPhoto(invitationId);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const guestName = (id: string | null) =>
    id ? (guests.find((g) => g.id === id)?.guest_name ?? 'Invitado') : 'Anónimo';

  const pendingCount = photos.filter((p) => !p.is_approved && !p.is_hidden).length;

  if (photos.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Álbum de invitados</CardTitle>
          <CardDescription>Aún no hay fotos subidas por tus invitados.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const state = (p: OwnerAlbumPhoto) => photoState(p.is_approved, p.is_hidden);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              Álbum de invitados
              {pendingCount > 0 && <Badge variant="secondary">{pendingCount} pendiente{pendingCount > 1 ? 's' : ''}</Badge>}
            </CardTitle>
            <CardDescription>{photos.length} foto{photos.length > 1 ? 's' : ''} en total</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => batch.mutate({ is_approved: true, is_hidden: false })} disabled={batch.isPending}>
              Aprobar todo
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => batch.mutate({ is_hidden: true })} disabled={batch.isPending}>
              Ocultar todo
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {photos.map((p) => {
            const st = state(p);
            const meta = STATE_LABEL[st];
            return (
              <div key={p.id} className="relative group border rounded-lg overflow-hidden">
                <img src={p.photo_url} alt={p.caption || 'Foto de invitado'} className={`w-full h-32 object-cover ${p.is_hidden ? 'opacity-50' : ''}`} />
                <div className="absolute top-1 left-1">
                  <Badge variant={meta.variant}>{meta.label}</Badge>
                </div>
                <div className="p-2 text-xs text-muted-foreground truncate" title={guestName(p.guest_id)}>
                  {guestName(p.guest_id)}
                </div>
                <div className="flex items-center justify-between gap-1 p-1 border-t bg-background">
                  {!p.is_approved && (
                    <Button type="button" variant="ghost" size="icon" title="Aprobar" onClick={() => moderate.mutate({ id: p.id, patch: { is_approved: true, is_hidden: false } })}>
                      <Check className="w-4 h-4" />
                    </Button>
                  )}
                  <Button type="button" variant="ghost" size="icon" title={p.is_hidden ? 'Mostrar' : 'Ocultar'} onClick={() => moderate.mutate({ id: p.id, patch: { is_hidden: !p.is_hidden } })}>
                    {p.is_hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </Button>
                  <AlertDialog open={confirmId === p.id} onOpenChange={(o) => setConfirmId(o ? p.id : null)}>
                    <AlertDialogTrigger asChild>
                      <Button type="button" variant="ghost" size="icon" title="Eliminar" className="text-destructive">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>¿Eliminar esta foto?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Esta acción es permanente y no se puede deshacer: la foto se borrará del álbum y del almacenamiento.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          onClick={() => del.mutate({ id: p.id, photoUrl: p.photo_url })}
                        >
                          Eliminar
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default AlbumModeration;
